import ToolbarButton from "../toolbar/ToolbarButton";
import { useState, useEffect } from "react";
import {
  useCurrentSimulationResults,
  useFireSimulationInspectFootprints,
  useSimulations,
} from "../../api/simulations";
import { useSimulation } from "../../contexts/SimulationContext";
import { useSelection } from "../../contexts/SelectionContext";
import { useEnsembleMember } from "../../contexts/EnsembleMemberContext";
import {useDate} from "../../contexts/DateContext";
import dayjs from "dayjs";
import {useTimezone} from "../../contexts/TimezoneContext.tsx";
import {
  detectGridSteps,
  footprintContains,
} from "../storyMap/simLayerData";

// Function to create a square polygon around a point
const createSquarePolygon = (
  lat: number,
  lng: number,
  latStep: number,
  lngStep: number
) => {
  return {
    type: "Polygon" as const,
    coordinates: [
      [
        [lng - lngStep, lat - latStep],
        [lng + lngStep, lat - latStep],
        [lng + lngStep, lat + latStep],
        [lng - lngStep, lat + latStep],
        [lng - lngStep, lat - latStep], // Close the polygon
      ],
    ],
  };
};

const isPointInPolygon = (
  point: { lng: number; lat: number },
  polygon: number[][][]
) => {
  const x = point.lng;
  const y = point.lat;
  const poly = polygon[0];
  let inside = false;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const xi = poly[i][0];
    const yi = poly[i][1];
    const xj = poly[j][0];
    const yj = poly[j][1];
    const intersect =
      yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi;
    if (intersect) inside = !inside;
  }
  return inside;
};

const partitionIndex = (ignition: any): number | null => {
  const idx = parseInt(String(ignition?.partition).split("_")[0], 10);
  return Number.isFinite(idx) ? idx : null;
};

const InspectionLayer = (id: string, name: string) => {
  const [active, setActive] = useState(false);
  const [geojson, setGeojson] = useState<any>(null);
  const [stale, setStale] = useState(false);
  const { selectedLatLng } = useSelection();
  const { selectedDate } = useDate();
  const { selectedTimezone } = useTimezone();
  const { data: simResults } = useCurrentSimulationResults();
  const { isSimulationComplete, currentSavedSimId } = useSimulation();
  const results = simResults?.data?.results ?? [];

  // Ensembles strip per-ignition final footprints at collation, so their
  // footprints come from the backend instead — fetched ONCE per (sim, view)
  // as soon as the layer is active (the slider-selected member's partitions,
  // or, for Confidence / not-engaged ensembles, the across-member union).
  // Every map click then hit-tests those polygons locally and instantly.
  const { data: simulationsData } = useSimulations();
  const savedSimRecord =
    simulationsData?.data?.find((s) => s.simulation_id === currentSavedSimId) ??
    null;
  const isEnsemble = Boolean(savedSimRecord?.is_ensemble);
  const { memberSelection } = useEnsembleMember();
  const inspectMemberIdx =
    memberSelection?.view === "member" ? memberSelection.memberIdx : null;
  const { data: footprintsResponse } = useFireSimulationInspectFootprints(
    currentSavedSimId,
    inspectMemberIdx,
    isEnsemble && active && isSimulationComplete
  );
  const footprintsData = footprintsResponse?.data ?? null;

  useEffect(() => {
    if (results.length > 0 && isSimulationComplete) {
      const selectedDateOnly = selectedDate.format("YYYY-MM-DD");
      const matchingData = results.find(
        (data) =>
          dayjs(data.start_date_time).tz(selectedTimezone).format("YYYY-MM-DD") === selectedDateOnly
      );

      if (!matchingData) {
        setGeojson(null);
        return;
      }

      setStale(true); // Set stale to true on data change
      const ignitions = matchingData.ignitions ?? [];
      // Cell size from the detected ignition-grid spacing (falls back to the
      // raster step) so gridded regimes render contiguous, joined-up cells.
      const { latStep: cellLat, lngStep: cellLng } = detectGridSteps({
        latStep: matchingData.lat_step,
        lngStep: matchingData.lng_step,
        ignitions: ignitions.map((ig: any) => ({
          lat: ig.start_point_lat,
          lng: ig.start_point_lng,
        })),
      });
      const latHalf = cellLat / 2;
      const lngHalf = cellLng / 2;

      if (isEnsemble) {
        // One cell per BASE ignition point (members replicate locations),
        // hit-tested locally against the prefetched footprints — instant.
        if (!footprintsData) {
          // Footprints still loading — keep whatever is on screen.
          return;
        }
        const ppm =
          footprintsData.points_per_member ??
          memberSelection?.pointsPerMember ??
          1;
        const footprintByPoint = new Map(
          footprintsData.footprints.map((f) => [f.point, f.footprint])
        );
        const seen = new Set<number>();
        const features: any[] = [];
        ignitions.forEach((ignition: any) => {
          const idx = partitionIndex(ignition);
          if (idx === null) return;
          const point = idx % ppm;
          if (seen.has(point)) return;
          seen.add(point);
          const footprint = footprintByPoint.get(point);
          if (!footprint) return; // unknown footprint — draw nothing
          const visible = selectedLatLng
            ? footprintContains(footprint as any, [
                selectedLatLng.lng,
                selectedLatLng.lat,
              ])
            : true;
          features.push({
            type: "Feature",
            geometry: createSquarePolygon(
              ignition.start_point_lat,
              ignition.start_point_lng,
              latHalf,
              lngHalf
            ),
            properties: {
              id: `inspection-${point}`,
              color: visible ? `#ffff00` : "transparent",
            },
          });
        });
        setGeojson({ type: "FeatureCollection", features });
        return;
      }

      const newGeojson = {
        type: "FeatureCollection",
        features: ignitions
          .map((ignition: any, index: number) => {
            const id = `inspection-${index}`;

            // Use final_animation_frame from result_details instead of full animation_frames
            if (ignition?.final_animation_frame?.features) {
              const finalFrameCollection = ignition.final_animation_frame;
              // Check if selectedLatLng is within any of the polygons
              const visible = selectedLatLng
                ? finalFrameCollection.features.some((feature: any) =>
                    feature.geometry &&
                    isPointInPolygon(
                      selectedLatLng,
                      feature.geometry.coordinates
                    )
                  )
                : true;
              return {
                type: "Feature",
                geometry: createSquarePolygon(
                  ignition.start_point_lat,
                  ignition.start_point_lng,
                  latHalf,
                  lngHalf
                ),
                properties: {
                  id,
                  color: visible ? `#ffff00` : "transparent",
                },
              };
            }
            return null; // Return null for invalid ignitions
          })
          .filter((feature) => feature !== null), // Filter out null values
      };
      setGeojson(newGeojson);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    results,
    isSimulationComplete,
    selectedDate,
    selectedLatLng,
    isEnsemble,
    footprintsData,
    memberSelection,
  ]);

  const fillLayerStyle = {
    id: id,
    type: "fill",
    paint: {
      "fill-color": ["get", "color"],
      "fill-opacity": 0.6,
    },
  };

  const toolbarButton = (
    <ToolbarButton
      key={id}
      id={id}
      name={name}
      toggledOn={active}
      // Ensembles fetch their footprints on first activation, so the button
      // must be usable before any geojson exists.
      enabled={(!!geojson || isEnsemble) && isSimulationComplete}
      onClick={() => setActive(!active)}
      icon={"search"}
    />
  );

  return {
    id: id,
    name: name,
    source: geojson,
    layerStyle: fillLayerStyle,
    toolbarButton: toolbarButton,
    active: active,
    stale: stale,
    setStale: setStale,
  };
};

export default InspectionLayer;
